import { Card, H3, Text, View } from "../base";
import { VoicePreview } from "./VoicePreview";
import { VoiceActions } from "./VoiceActions";
import { OrganizationCustomVoice } from "../../redux/reducers/organizationCustomVoicesSlice";
import { UpdateCustomVoiceDialogue } from "../voices/UpdateCustomVoiceDialogue";
import { ConfirmDialog } from "../ConfirmDialog";
import { useCallback, useState } from "react";
import { getOrganizationFile } from "../../utils/organizationFiles";

const LANGUAGE_NAME_MAP = {
  en: "English",
  fr: "French",
  de: "German",
  es: "Spanish",
  pt: "Portuguese",
  zh: "Chinese",
  ja: "Japanese",
  hi: "Hindi",
  it: "Italian",
  ko: "Korean",
  nl: "Dutch",
  pl: "Polish",
  ru: "Russian",
  sv: "Swedish",
  tr: "Turkish",
} satisfies Record<string, string>;

type CustomVoiceCardProps = {
  voice: OrganizationCustomVoice;
  isActive: boolean;
  onPlaySample: (key: string, audio: HTMLAudioElement) => void;
  onUpdateVoice: (id: string, params: { name: string; description: string }) => void;
  onDeleteVoice: (id: string) => void;
};

export const CustomVoiceCard = ({ voice, isActive, onPlaySample, onUpdateVoice, onDeleteVoice }: CustomVoiceCardProps) => {
  const [showEditVoiceDialog, setShowEditVoiceDialog] = useState(false);
  const [editingVoice, setEditingVoice] = useState<OrganizationCustomVoice | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const onClickEditVoice = (voice: OrganizationCustomVoice) => {
    setEditingVoice(voice);
    setShowEditVoiceDialog(true);
  };

  const handleCloseEditVoiceDialog = () => {
    setShowEditVoiceDialog(false);
    setEditingVoice(null);
  };

  const onClickDeleteVoice = (voiceId: string) => {
    setShowDeleteDialog(true);
  };

  const confirmDeleteVoice = () => {
    onDeleteVoice(voice.id);
    setShowDeleteDialog(false);
  };

  const downloadVoiceAudio = useCallback(async (audioUrl: string | null | undefined, filenameBase: string) => {
    if (!audioUrl) {
      return;
    }

    try {
      let downloadUrl = audioUrl;
      let revokeDownloadUrl: (() => void) | null = null;

      if (!audioUrl.startsWith("blob:")) {
        if (audioUrl.includes("/v1/organizations/")) {
          const { objectUrl, revokeObjectUrl } = await getOrganizationFile(audioUrl);
          downloadUrl = objectUrl;
          revokeDownloadUrl = revokeObjectUrl;
        } else {
          const audioResponse = await fetch(audioUrl);
          if (!audioResponse.ok) {
            throw new Error(`Failed to fetch audio from provided URL: ${audioResponse.status}`);
          }

          const blob = await audioResponse.blob();
          downloadUrl = URL.createObjectURL(blob);
          revokeDownloadUrl = () => URL.revokeObjectURL(downloadUrl);
        }
      }

      let extension = "mp3";
      try {
        if (!audioUrl.startsWith("blob:")) {
          const pathname = new URL(audioUrl).pathname;
          const possibleExtension = pathname.split(".").pop();
          if (possibleExtension && possibleExtension.length <= 5) {
            extension = possibleExtension;
          }
        }
      } catch (error) {
        console.error("Error inferring file extension:", error);
      }

      const sanitizedBase = filenameBase
        .replace(/[^a-zA-Z0-9-_]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
      const downloadName = `${sanitizedBase || "voice-sample"}.${extension}`;

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (revokeDownloadUrl) {
        revokeDownloadUrl();
      }
    } catch (error) {
      console.error("Error downloading voice sample:", error);
    }
  }, []);

  const normalizedLanguage: string = voice.language?.toLowerCase();
  const languageName = normalizedLanguage ? LANGUAGE_NAME_MAP[normalizedLanguage as keyof typeof LANGUAGE_NAME_MAP] : undefined;

  return (
    <Card className="p-5 space-y-4 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm bg-white dark:bg-gray-900/80">
      <View className="flex gap-6 flex-row items-start">
        <View className="w-16 flex-shrink-0 self-center flex items-center justify-center">
          <VoicePreview
            variant="button"
            audioUrl={voice.previewAudioUrl}
            audioKey={`${voice.id}-preview`}
            onPlaySample={onPlaySample}
            isActive={isActive}
            showDownload={false}
          />
        </View>

        <View className="flex flex-1 flex-col gap-3">
          <View className="flex flex-row gap-3 items-start justify-between">
            <View className="flex flex-1 flex-col gap-1">
              <H3 className="leading-tight">{voice.name}</H3>
              {(languageName || voice.description) && (
                <View className="flex items-start flex-row gap-2">
                  {languageName && <Text className="text-sm text-gray-600 dark:text-gray-300 font-bold self-center">{languageName}</Text>}
                  {languageName && voice.description && <Text className="text-xs text-gray-600 dark:text-gray-300 self-center">•</Text>}
                  {voice.description && <Text className="text-sm text-gray-600 dark:text-gray-300 self-center">{voice.description}</Text>}
                </View>
              )}
            </View>
            <VoiceActions
              voiceName={voice.name}
              onEdit={() => onClickEditVoice(voice)}
              onDelete={() => onClickDeleteVoice(voice.id)}
              onDownloadPreview={
                voice.previewAudioUrl ? () => downloadVoiceAudio(voice.previewAudioUrl, `${voice.name?.trim() || voice.id}-preview`) : undefined
              }
              onDownloadOriginal={
                voice.originalAudioSampleUrl ? () => downloadVoiceAudio(voice.originalAudioSampleUrl, `${voice.name?.trim() || voice.id}-original`) : undefined
              }
              hasPreview={true}
              hasOriginal={Boolean(voice.originalAudioSampleUrl)}
            />
          </View>
        </View>
      </View>
      <UpdateCustomVoiceDialogue
        isOpen={showEditVoiceDialog}
        voice={editingVoice}
        onClose={handleCloseEditVoiceDialog}
        onUpdateVoice={onUpdateVoice}
      />
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDeleteVoice}
        title="Delete Custom Voice"
        message={`Are you sure you want to delete "${voice.name}"?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone. The custom voice will be permanently removed.</Text>
      </ConfirmDialog>
    </Card>
  );
};
